import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import type { HtmlEditorUploadedImage } from "@entrilia/shared/components/html/htmlEditorTypes";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useTabSearchParams from "@entrilia/shared/hooks/useTabSearchParams";
import { logError } from "@entrilia/shared/logging";
import { arrayToMap } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../../api/adminApi";
import blobStorageApi from "../../../../api/blobStorageApi";
import type { Category } from "../../../../api/types/accessTypes";
import type {
  InvestorCommunication,
  InvestorCommunicationEngagement,
  InvestorCommunicationRecipient,
  InvestorCommunicationType,
} from "../../../../api/types/investorCommunicationsTypes";
import type { LookupObjectReference } from "../../../../api/types/objectTypes";
import PageTabPanel from "../../../common/PageTabPanel";
import PageTabs from "../../../common/PageTabs";
import type { VideoFrameSource } from "../../../../utilities/captureVideoFrame";
import { captureVideoFrame } from "../../../../utilities/captureVideoFrame";
import { getThumbnailFileName } from "../../../../utilities/videoThumbnailFrame";
import ChangeInvestorCommunicationStatusDialog from "../ChangeInvestorCommunicationStatusDialog";
import DeleteInvestorCommunicationDialog from "../DeleteInvestorCommunicationDialog";
import type { InvestorCommunicationStatusTransition } from "../investorCommunicationStatusHelper";
import ConfirmSaveLiveInvestorCommunicationDialog from "./ConfirmSaveLiveInvestorCommunicationDialog";
import InvestorCommunicationContentTab from "./content/InvestorCommunicationContentTab";
import { getBodyImageRejectionReason } from "./content/investorCommunicationFiles";
import { InvestorCommunicationContextProvider } from "./InvestorCommunicationContext";
import InvestorCommunicationDetailsPageHeader from "./InvestorCommunicationDetailsPageHeader";
import { detailsPageTabs, type CommunicationImageSource, type SaveAction } from "./investorCommunicationDetailsTypes";
import type { ArticleEditState } from "./investorCommunicationArticleEditState";
import { removeHeroImageAction, setHeroImageFileAction } from "./investorCommunicationArticleEditState";
import {
  applyPartiallySavedCommunicationAction,
  applySavedCommunicationAction,
  discardChangesAction,
  getInitialInvestorCommunicationEditState,
  getUpdateInvestorCommunicationRequest,
  isContentStepCompleted,
  isDirty,
  isRecipientsStepCompleted,
  isValid,
  validateEditState,
} from "./investorCommunicationEditState";
import type { VideoEditState } from "./investorCommunicationVideoEditState";
import {
  applyVideoCompletionErrorAction,
  isThumbnailDeletionPending,
  isVideoRemovalPending,
  needsGeneratedThumbnail,
  removeThumbnailFileAction,
  removeVideoFileAction,
  setThumbnailFileAction,
  setUploadedVideoFileIdAction,
  setVideoFileAction,
} from "./investorCommunicationVideoEditState";
import InvestorCommunicationRecipientsTab from "./recipients/InvestorCommunicationRecipientsTab";
import InvestorCommunicationSummary from "./summary/InvestorCommunicationSummary";
import InvestorCommunicationTabLabel from "./InvestorCommunicationTabLabel";
import InvestorCommunicationTimingTab from "./timing/InvestorCommunicationTimingTab";

const updateInvestorCommunication = withErrorHandling(adminApi.updateInvestorCommunication);
const uploadHeroImage = withErrorHandling(adminApi.uploadInvestorCommunicationHeroImage);
const deleteHeroImage = withErrorHandling(adminApi.deleteInvestorCommunicationHeroImage);
const uploadBodyImage = withErrorHandling(adminApi.uploadInvestorCommunicationBodyImage);
const getVideoUploadUrl = withErrorHandling(adminApi.getInvestorCommunicationVideoUploadUrl);
const uploadVideoToBlobStorage = withErrorHandling(blobStorageApi.uploadFileToBlobStorage);
const completeVideoUpload = withErrorHandling(adminApi.completeInvestorCommunicationVideoUpload);
const uploadThumbnail = withErrorHandling(adminApi.uploadInvestorCommunicationVideoThumbnail);
const getInvestorCommunication = withErrorHandling(adminApi.getInvestorCommunicationById);
const deleteVideo = withErrorHandling(adminApi.deleteInvestorCommunicationVideo);
const deleteThumbnail = withErrorHandling(adminApi.deleteInvestorCommunicationVideoThumbnail);

interface SaveResult {
  communication: InvestorCommunication;
  areFilesApplied: boolean;
}

const getSavedHeroImageSource = (communication: InvestorCommunication): CommunicationImageSource | undefined => {
  const fileId = communication.articleContent?.heroImageFileId;
  return fileId === undefined ? undefined : { kind: "saved", communicationId: communication.id, fileId };
};

const getDraftHeroImageSource = (
  articleState: ArticleEditState,
  pendingHeroImageUrl: string | undefined,
  savedSource: CommunicationImageSource | undefined
): CommunicationImageSource | undefined => {
  if (articleState.heroImageFile && pendingHeroImageUrl) {
    return { kind: "local", url: pendingHeroImageUrl, fileName: articleState.heroImageFile.name };
  }

  return articleState.isHeroImageRemoved ? undefined : savedSource;
};

const getSavedThumbnailSource = (communication: InvestorCommunication): CommunicationImageSource | undefined => {
  const url = communication.videoContent?.thumbnailUrl;
  return url === undefined ? undefined : { kind: "url", url };
};

const getDraftThumbnailSource = (
  videoState: VideoEditState,
  pendingThumbnailUrl: string | undefined,
  savedSource: CommunicationImageSource | undefined
): CommunicationImageSource | undefined => {
  if (videoState.thumbnailFile && pendingThumbnailUrl) {
    return { kind: "local", url: pendingThumbnailUrl, fileName: videoState.thumbnailFile.name };
  }

  return videoState.isThumbnailRemoved ? undefined : savedSource;
};

const getDraftVideoUrl = (
  videoState: VideoEditState | undefined,
  pendingVideoUrl: string | undefined,
  savedCommunication: InvestorCommunication
): string | undefined => {
  if (videoState?.videoFile && pendingVideoUrl) {
    return pendingVideoUrl;
  }

  return videoState?.isVideoRemoved ? undefined : savedCommunication.videoContent?.videoUrl;
};

const thumbnailCaptureFailedError = "Couldn't take a frame from the video. Upload a thumbnail instead.";

const getVideoFrameSource = (
  videoFile: File | undefined,
  videoUrl: string | undefined
): VideoFrameSource | undefined => {
  if (videoFile) {
    return { file: videoFile };
  }

  return videoUrl === undefined ? undefined : { url: videoUrl };
};

const contentIncompleteHints: Record<InvestorCommunicationType, string> = {
  Article: "Add a title and body content",
  Video: "Add a title, a video and a thumbnail (taken from the video unless you upload one)",
};

interface Props {
  investorCommunication: InvestorCommunication;
  recipients: InvestorCommunicationRecipient[];
  engagement?: InvestorCommunicationEngagement;
  funds: LookupObjectReference[];
  categories: Category[];
  hasEditPermissions: boolean;
  isNewsVisibleToInvestors: boolean;
  onSaved: (communication: InvestorCommunication) => void;
  onRecipientsRefresh: () => Promise<void>;
}

const InvestorCommunicationDetails = ({
  investorCommunication,
  recipients,
  engagement,
  funds,
  categories,
  hasEditPermissions,
  isNewsVisibleToInvestors,
  onSaved,
  onRecipientsRefresh,
}: Props) => {
  const navigate = useNavigate();
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [state, setState] = useState(() => getInitialInvestorCommunicationEditState(investorCommunication));
  const [pendingHeroImageUrl, setPendingHeroImageUrl] = useState<string>();
  const [pendingVideoUrl, setPendingVideoUrl] = useState<string>();
  const [pendingThumbnailUrl, setPendingThumbnailUrl] = useState<string>();
  const [videoUploadProgress, setVideoUploadProgress] = useState<number>();
  const [thumbnailCaptureError, setThumbnailCaptureError] = useState<string>();
  const [isGeneratingThumbnail, setIsGeneratingThumbnail] = useState(false);
  const [pendingSaveAction, setPendingSaveAction] = useState<SaveAction>();
  const [unconfirmedSaveAction, setUnconfirmedSaveAction] = useState<SaveAction>();
  const [pendingImageUploads, setPendingImageUploads] = useState(0);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [statusTransition, setStatusTransition] = useState<InvestorCommunicationStatusTransition>();

  const { selectedTab, onTabChange } = useTabSearchParams(detailsPageTabs);

  const readersByContactId = useMemo(
    () =>
      engagement &&
      arrayToMap(
        engagement.readers,
        (reader) => reader.contactId,
        (reader) => reader
      ),
    [engagement]
  );

  const isSaving = pendingSaveAction !== undefined;
  const hasPendingImageUploads = pendingImageUploads > 0;
  const hasChanges = isDirty(state);
  const validationErrors = validateEditState(state);
  const isSaveAllowed = hasChanges && isValid(state) && !hasPendingImageUploads;

  const { typeState } = state;
  const articleState = typeState.type === "Article" ? typeState : undefined;
  const videoState = typeState.type === "Video" ? typeState : undefined;

  const savedHeroImageSource = getSavedHeroImageSource(state.savedCommunication);
  const draftHeroImageSource =
    articleState && getDraftHeroImageSource(articleState, pendingHeroImageUrl, savedHeroImageSource);
  const savedThumbnailSource = getSavedThumbnailSource(state.savedCommunication);
  const draftThumbnailSource =
    videoState && getDraftThumbnailSource(videoState, pendingThumbnailUrl, savedThumbnailSource);
  const draftVideoUrl = getDraftVideoUrl(videoState, pendingVideoUrl, state.savedCommunication);

  const releasePendingHeroImageUrl = () => {
    if (pendingHeroImageUrl) {
      URL.revokeObjectURL(pendingHeroImageUrl);
    }

    setPendingHeroImageUrl(undefined);
  };

  const releasePendingVideoUrl = () => {
    if (pendingVideoUrl) {
      URL.revokeObjectURL(pendingVideoUrl);
    }

    setPendingVideoUrl(undefined);
  };

  const releasePendingThumbnailUrl = () => {
    if (pendingThumbnailUrl) {
      URL.revokeObjectURL(pendingThumbnailUrl);
    }

    setPendingThumbnailUrl(undefined);
  };

  const releasePendingFileUrls = () => {
    releasePendingHeroImageUrl();
    releasePendingVideoUrl();
    releasePendingThumbnailUrl();
  };

  const handleHeroImageFileAdd = (file: File) => {
    releasePendingHeroImageUrl();
    setPendingHeroImageUrl(URL.createObjectURL(file));
    setState(setHeroImageFileAction(file));
  };

  const handleHeroImageRemove = () => {
    releasePendingHeroImageUrl();
    setState(removeHeroImageAction());
  };

  const handleVideoFileAdd = (file: File) => {
    releasePendingVideoUrl();
    setPendingVideoUrl(URL.createObjectURL(file));
    setThumbnailCaptureError(undefined);
    setState(setVideoFileAction(file));
  };

  const handleVideoFileRemove = () => {
    releasePendingVideoUrl();
    setThumbnailCaptureError(undefined);
    setState(removeVideoFileAction());
  };

  const handleThumbnailFileAdd = (file: File) => {
    releasePendingThumbnailUrl();
    setPendingThumbnailUrl(URL.createObjectURL(file));
    setThumbnailCaptureError(undefined);
    setState(setThumbnailFileAction(file));
  };

  const handleThumbnailFileRemove = () => {
    releasePendingThumbnailUrl();
    setState(removeThumbnailFileAction());
  };

  const handleDiscardChanges = () => {
    releasePendingFileUrls();
    setThumbnailCaptureError(undefined);
    setState(discardChangesAction());
  };

  const uploadAcceptedBodyImage = async (file: File): Promise<HtmlEditorUploadedImage | undefined> => {
    const [image, error] = await uploadBodyImage(investorCommunication.id, file);

    if (error) {
      logError(error, "[InvestorCommunicationDetails] uploadInvestorCommunicationBodyImage");
      sendNotificationError(`${file.name}: ${error.message}`);
      return undefined;
    }

    return { fileId: image.fileId, url: image.url, fileName: file.name };
  };

  const handleBodyImagesUpload = async (files: File[]): Promise<HtmlEditorUploadedImage[]> => {
    const acceptedFiles: File[] = [];
    files.forEach((file) => {
      const rejectionReason = getBodyImageRejectionReason(file);
      if (rejectionReason) {
        sendNotificationError(`${file.name}: ${rejectionReason}`);
      } else {
        acceptedFiles.push(file);
      }
    });

    if (acceptedFiles.length === 0) {
      return [];
    }

    setPendingImageUploads((count) => count + 1);
    try {
      const uploadedImages = await Promise.all(acceptedFiles.map(uploadAcceptedBodyImage));
      return uploadedImages.filter((image) => image !== undefined);
    } finally {
      setPendingImageUploads((count) => count - 1);
    }
  };

  const applyHeroImageChanges = async (
    communication: InvestorCommunication,
    { heroImageFile, isHeroImageRemoved }: ArticleEditState
  ): Promise<SaveResult> => {
    if (heroImageFile) {
      const [updated, error] = await uploadHeroImage(investorCommunication.id, heroImageFile, communication.etag);

      if (error) {
        logError(error, "[InvestorCommunicationDetails] uploadInvestorCommunicationHeroImage");
        sendNotificationError(error.message);
        return { communication, areFilesApplied: false };
      }

      return { communication: updated, areFilesApplied: true };
    }

    if (isHeroImageRemoved && state.savedCommunication.articleContent?.heroImageFileId !== undefined) {
      const [updated, error] = await deleteHeroImage(investorCommunication.id, communication.etag);

      if (error) {
        logError(error, "[InvestorCommunicationDetails] deleteInvestorCommunicationHeroImage");
        sendNotificationError(error.message);
        return { communication, areFilesApplied: false };
      }

      return { communication: updated, areFilesApplied: true };
    }

    return { communication, areFilesApplied: true };
  };

  const uploadVideoFile = async (videoFile: File): Promise<string | undefined> => {
    const [uploadInfo, uploadUrlError] = await getVideoUploadUrl(investorCommunication.id, videoFile.name);

    if (uploadUrlError) {
      logError(uploadUrlError, "[InvestorCommunicationDetails] getInvestorCommunicationVideoUploadUrl");
      sendNotificationError(uploadUrlError.message);
      return undefined;
    }

    setVideoUploadProgress(0);
    const [, uploadError] = await uploadVideoToBlobStorage(uploadInfo.uploadUrl, videoFile, (e) =>
      setVideoUploadProgress(e.progress ?? 0)
    );

    if (uploadError) {
      logError(uploadError, "[InvestorCommunicationDetails] uploadFileToBlobStorage");
      sendNotificationError("Failed to upload the video");
      return undefined;
    }

    setState(setUploadedVideoFileIdAction(uploadInfo.fileId));
    return uploadInfo.fileId;
  };

  const attachVideoFile = async (
    communication: InvestorCommunication,
    videoFile: File,
    uploadedVideoFileId: string | undefined
  ): Promise<InvestorCommunication | undefined> => {
    const fileId = uploadedVideoFileId ?? (await uploadVideoFile(videoFile));

    if (fileId === undefined) {
      return undefined;
    }

    const [completed, completeError] = await completeVideoUpload(investorCommunication.id, fileId, communication.etag);

    if (!completeError) {
      return completed;
    }

    logError(completeError, "[InvestorCommunicationDetails] completeInvestorCommunicationVideoUpload");

    // The attach may have committed even though the response reported an error.
    const [refetched, refetchError] = await getInvestorCommunication(investorCommunication.id);

    if (refetchError) {
      logError(refetchError, "[InvestorCommunicationDetails] getInvestorCommunicationById");
    } else if (refetched.videoContent?.videoFileId === fileId) {
      return refetched;
    }

    setState(applyVideoCompletionErrorAction(completeError.message));
    sendNotificationError(completeError.message);
    return undefined;
  };

  const removeVideoFile = async (
    communication: InvestorCommunication,
    remove: typeof deleteVideo,
    operation: string
  ): Promise<InvestorCommunication | undefined> => {
    const [updated, error] = await remove(investorCommunication.id, communication.etag);

    if (error) {
      logError(error, `[InvestorCommunicationDetails] ${operation}`);
      sendNotificationError(error.message);
      return undefined;
    }

    return updated;
  };

  const reportThumbnailCaptureFailure = (error: unknown) => {
    logError(error, "[InvestorCommunicationDetails] captureVideoFrame");
    setThumbnailCaptureError(thumbnailCaptureFailedError);
    sendNotificationError(thumbnailCaptureFailedError);
  };

  const captureThumbnailFromVideo = async (
    communication: InvestorCommunication,
    videoFile: File | undefined
  ): Promise<File | undefined> => {
    const { videoUrl, fileName } = communication.videoContent ?? {};
    const source = getVideoFrameSource(videoFile, videoUrl);

    if (source === undefined) {
      reportThumbnailCaptureFailure(new Error("The saved video has no URL to read a frame from"));
      return undefined;
    }

    try {
      return await captureVideoFrame(source, getThumbnailFileName(videoFile?.name ?? fileName ?? ""));
    } catch (error) {
      reportThumbnailCaptureFailure(error);
      return undefined;
    }
  };

  const uploadThumbnailFile = async (
    communication: InvestorCommunication,
    thumbnailFile: File
  ): Promise<InvestorCommunication | undefined> => {
    const [updated, error] = await uploadThumbnail(investorCommunication.id, thumbnailFile, communication.etag);

    if (error) {
      logError(error, "[InvestorCommunicationDetails] uploadInvestorCommunicationVideoThumbnail");
      sendNotificationError(error.message);
      return undefined;
    }

    return updated;
  };

  const applyGeneratedThumbnail = async (
    communication: InvestorCommunication,
    videoFile: File | undefined
  ): Promise<InvestorCommunication | undefined> => {
    setIsGeneratingThumbnail(true);
    try {
      const capturedThumbnail = await captureThumbnailFromVideo(communication, videoFile);

      if (capturedThumbnail === undefined) {
        return undefined;
      }

      const updated = await uploadThumbnailFile(communication, capturedThumbnail);

      if (updated === undefined) {
        // Keep the captured frame as the pending thumbnail so the next Save retries the upload without re-capturing.
        handleThumbnailFileAdd(capturedThumbnail);
      }

      return updated;
    } finally {
      setIsGeneratingThumbnail(false);
    }
  };

  const applyVideoChanges = async (
    communication: InvestorCommunication,
    videoState: VideoEditState
  ): Promise<SaveResult> => {
    const { videoFile, uploadedVideoFileId, thumbnailFile } = videoState;
    let latestCommunication = communication;

    if (isVideoRemovalPending(videoState, state.savedCommunication)) {
      const updated = await removeVideoFile(latestCommunication, deleteVideo, "deleteInvestorCommunicationVideo");

      if (updated === undefined) {
        return { communication: latestCommunication, areFilesApplied: false };
      }

      latestCommunication = updated;
    }

    if (isThumbnailDeletionPending(videoState, state.savedCommunication)) {
      const updated = await removeVideoFile(
        latestCommunication,
        deleteThumbnail,
        "deleteInvestorCommunicationVideoThumbnail"
      );

      if (updated === undefined) {
        return { communication: latestCommunication, areFilesApplied: false };
      }

      latestCommunication = updated;
    }

    if (videoFile) {
      try {
        const attached = await attachVideoFile(latestCommunication, videoFile, uploadedVideoFileId);

        if (attached === undefined) {
          return { communication: latestCommunication, areFilesApplied: false };
        }

        latestCommunication = attached;
      } finally {
        setVideoUploadProgress(undefined);
      }
    }

    if (thumbnailFile) {
      const updated = await uploadThumbnailFile(latestCommunication, thumbnailFile);

      if (updated === undefined) {
        return { communication: latestCommunication, areFilesApplied: false };
      }

      latestCommunication = updated;
    }

    if (needsGeneratedThumbnail(videoState, state.savedCommunication, latestCommunication)) {
      const updated = await applyGeneratedThumbnail(latestCommunication, videoFile);

      if (updated === undefined) {
        return { communication: latestCommunication, areFilesApplied: false };
      }

      latestCommunication = updated;
    }

    return { communication: latestCommunication, areFilesApplied: true };
  };

  const applyFileChanges = (communication: InvestorCommunication): Promise<SaveResult> => {
    switch (typeState.type) {
      case "Article":
        return applyHeroImageChanges(communication, typeState);
      case "Video":
        return applyVideoChanges(communication, typeState);
    }
  };

  const saveChanges = async (): Promise<SaveResult | undefined> => {
    const updateRequest = getUpdateInvestorCommunicationRequest(state);

    if (updateRequest === undefined) {
      return applyFileChanges(state.savedCommunication);
    }

    const [updated, error] = await updateInvestorCommunication(investorCommunication.id, updateRequest);

    if (error) {
      logError(error, "[InvestorCommunicationDetails] updateInvestorCommunication");
      sendNotificationError(error.message);
      return undefined;
    }

    return applyFileChanges(updated);
  };

  const handleSave = async (saveAction: SaveAction) => {
    if (!isSaveAllowed) {
      return;
    }

    setPendingSaveAction(saveAction);
    setThumbnailCaptureError(undefined);
    const result = await saveChanges();

    if (result === undefined) {
      setPendingSaveAction(undefined);
      return;
    }

    const { communication, areFilesApplied } = result;

    onSaved(communication);
    await onRecipientsRefresh();

    if (areFilesApplied) {
      setState(applySavedCommunicationAction(communication));
      releasePendingFileUrls();
    } else {
      setState(applyPartiallySavedCommunicationAction(communication));
      if (communication.videoContent?.videoFileId !== state.savedCommunication.videoContent?.videoFileId) {
        releasePendingVideoUrl();
      }
    }
    setPendingSaveAction(undefined);

    if (!areFilesApplied) {
      return;
    }

    sendNotification("Changes have been saved");

    if (saveAction === "saveAndExit") {
      navigate("..");
    }
  };

  const handleSaveRequest = (saveAction: SaveAction) => {
    if (state.savedCommunication.status === "Live") {
      setUnconfirmedSaveAction(saveAction);
      return;
    }

    handleSave(saveAction);
  };

  const handleSaveConfirmed = (saveAction: SaveAction) => {
    setUnconfirmedSaveAction(undefined);
    handleSave(saveAction);
  };

  const handleStatusUpdated = (communication: InvestorCommunication) => {
    onSaved(communication);
    setState(applySavedCommunicationAction(communication));
    setStatusTransition(undefined);
  };

  const handleDeleted = () => {
    setIsDeleteDialogOpen(false);
    navigate("..");
  };

  const { savedCommunication } = state;

  return (
    <InvestorCommunicationContextProvider
      investorCommunication={savedCommunication}
      recipients={recipients}
      engagement={engagement}
      readersByContactId={readersByContactId}
      funds={funds}
      categories={categories}
      hasEditPermissions={hasEditPermissions}
      isNewsVisibleToInvestors={isNewsVisibleToInvestors}
      savedHeroImageSource={savedHeroImageSource}
      draftHeroImageSource={draftHeroImageSource}
      savedThumbnailSource={savedThumbnailSource}
      draftThumbnailSource={draftThumbnailSource}
      draftVideoUrl={draftVideoUrl}
      thumbnailCaptureError={thumbnailCaptureError}
      isGeneratingThumbnail={isGeneratingThumbnail}
      videoUploadProgress={videoUploadProgress}
      state={state}
      setState={setState}
      hasChanges={hasChanges}
      isSaveAllowed={isSaveAllowed}
      validationErrors={validationErrors}
      isSaving={isSaving}
      hasPendingImageUploads={hasPendingImageUploads}
      pendingSaveAction={pendingSaveAction}
      selectedTab={selectedTab}
      onTabChange={onTabChange}
      onStatusChange={setStatusTransition}
      onDelete={() => setIsDeleteDialogOpen(true)}
      onDiscardChanges={handleDiscardChanges}
      onSave={() => handleSaveRequest("save")}
      onSaveAndExit={() => handleSaveRequest("saveAndExit")}
      onHeroImageFileAdd={handleHeroImageFileAdd}
      onHeroImageRemove={handleHeroImageRemove}
      onVideoFileAdd={handleVideoFileAdd}
      onVideoFileRemove={handleVideoFileRemove}
      onThumbnailFileAdd={handleThumbnailFileAdd}
      onThumbnailFileRemove={handleThumbnailFileRemove}
      onBodyImagesUpload={handleBodyImagesUpload}
    >
      <InvestorCommunicationDetailsPageHeader />

      <PageTabs
        tabs={detailsPageTabs}
        labels={[
          "Summary",
          <InvestorCommunicationTabLabel
            key="recipients"
            label="Recipients"
            count={recipients.length}
            isActive={selectedTab === "recipients"}
            incompleteHint={isRecipientsStepCompleted(state) ? undefined : "Select at least one fund and one category"}
          />,
          <InvestorCommunicationTabLabel
            key="content"
            label="Content"
            incompleteHint={isContentStepCompleted(state) ? undefined : contentIncompleteHints[savedCommunication.type]}
          />,
          <InvestorCommunicationTabLabel key="timing" label="Timing" incompleteHint={validationErrors.publishedAt} />,
        ]}
        value={selectedTab}
        disabledTabs={hasPendingImageUploads ? detailsPageTabs.filter((tab) => tab !== selectedTab) : undefined}
        onChange={onTabChange}
      >
        <PageTabPanel value="summary" containerWidth="lg">
          <InvestorCommunicationSummary />
        </PageTabPanel>
        <PageTabPanel value="recipients" containerWidth="lg">
          <InvestorCommunicationRecipientsTab />
        </PageTabPanel>
        <PageTabPanel value="content" containerWidth="lg">
          <InvestorCommunicationContentTab />
        </PageTabPanel>
        <PageTabPanel value="timing" containerWidth="lg">
          <InvestorCommunicationTimingTab />
        </PageTabPanel>
      </PageTabs>

      {statusTransition && (
        <ChangeInvestorCommunicationStatusDialog
          investorCommunicationId={savedCommunication.id}
          expectedEtag={savedCommunication.etag}
          statusTransition={statusTransition}
          onClose={() => setStatusTransition(undefined)}
          onUpdated={handleStatusUpdated}
        />
      )}

      {unconfirmedSaveAction && (
        <ConfirmSaveLiveInvestorCommunicationDialog
          isNewsVisibleToInvestors={isNewsVisibleToInvestors}
          onClose={() => setUnconfirmedSaveAction(undefined)}
          onSaveConfirmed={() => handleSaveConfirmed(unconfirmedSaveAction)}
        />
      )}

      {isDeleteDialogOpen && (
        <DeleteInvestorCommunicationDialog
          investorCommunicationId={savedCommunication.id}
          investorCommunicationTitle={savedCommunication.content?.title || "Untitled"}
          onClose={() => setIsDeleteDialogOpen(false)}
          onDeleted={handleDeleted}
        />
      )}
    </InvestorCommunicationContextProvider>
  );
};

export default InvestorCommunicationDetails;
